import {
  Flex,
  IconButton,
  TextField,
  Select,
  Button,
  TextArea,
  Spinner,
  Text,
  Card,
  Callout,
  Separator
} from '@radix-ui/themes'
import { DataSource, ManualDataSource, ManualDataSourceSchema } from '@willie/schemas'
import { DateTime } from 'luxon'
import { ManualDatapoint } from '../../schemas'
import { useEffect, useMemo, useState } from 'react'
import DatePickerField from '~/components/DatePickerField'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { LuDownload, LuFile, LuFileSpreadsheet, LuPlus, LuX } from 'react-icons/lu'
import { useTranslation } from 'react-i18next'
import { FieldWrapper } from '~/components/FormFieldWrapper'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createOrUpdateDatasource,
  deleteManualDatapoints,
  insertManualDatapoints,
  listDatapointsPayloadByDeviceID
} from '../../datasources-service'
import { useCurrentOrg } from '~/features/orgnisations/hooks/useCurrentOrg'
import { toast } from 'sonner'
import Sentry from '~/tools/sentry'
import { useAsync, usePrevious } from 'react-use'
import { EmptyState } from '~/components/EmptyState'
import { uniqueId } from 'lodash'
import CSVImportDialog from './CSVImportDialog'
import { OrgAssetMeta } from '~/features/orgnisations/service'
import { useSupabaseClient } from '~/hooks/useSupabaseClient'
import SecuredOrgImage from '~/components/SecuredOrgImage'

type StorageFileApi = ReturnType<Awaited<ReturnType<typeof useSupabaseClient>['storage']['from']>>
type ObjectInfo = NonNullable<Awaited<ReturnType<StorageFileApi['info']>>['data']>

const AttachmentPreview = ({ path }: { path: string }) => {
  const filePath = path
  const supabase = useSupabaseClient()
  const [attachmentInfo, setAttachmentInfo] = useState<ObjectInfo | null>(null)
  const [downloadURL, setDownloadURL] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)

  useAsync(async () => {
    const { data: info } = await supabase.storage.from('org_content').info(filePath)
    if (!info) {
      console.warn('No file info found for path:', filePath)
      return
    }
    setAttachmentInfo(info)

    const { data } = await supabase.storage
      .from('org_content')
      .createSignedUrl(filePath, 60 * 5, { download: true })

    if (data?.signedUrl) {
      setDownloadURL(data.signedUrl)
    }
  }, [])

  return (
    <Card size="2">
      <Flex align="end" gap={'3'} className="group">
        {attachmentInfo?.contentType?.includes('image') ? (
          <SecuredOrgImage fileName={path} zoomable imgProps={{ className: 'w-10' }} />
        ) : attachmentInfo?.contentType?.includes('csv') ? (
          <LuFileSpreadsheet size={45} className="text-blue-9 stroke-1" />
        ) : (
          <LuFile size={45} className="text-blue-9 stroke-1" />
        )}
        <Flex direction={'column'}>
          <Text size="3">{filePath.split('/').pop()}</Text>
          <Text size="2" color="gray">
            {DateTime.fromISO(attachmentInfo?.createdAt ?? '').toLocaleString(DateTime.DATE_MED, {
              locale: 'fr-FR'
            })}
          </Text>
        </Flex>
        {downloadURL && (
          <a href={downloadURL ?? '#'} className="ml-auto self-center" aria-label="Télécharger le fichier">
            <IconButton
              size={'3'}
              type="button"
              color="gray"
              variant="ghost"
              loading={downloading}
              onClick={() => {
                setDownloading(true)
                setTimeout(() => setDownloading(false), 1500)
              }}
            >
              <LuDownload size={22} />
            </IconButton>
          </a>
        )}
      </Flex>
    </Card>
  )
}

type InlineDatapointFormFieldProps = {
  value?: ManualDatapoint
  onChange?: (value: ManualDatapoint) => void
  onDelete?: () => void
  disabled?: boolean
  unit: DataSource['unit']
}

const DEFAULT_DATAPOINT: ManualDatapoint = {
  date: DateTime.now(),
  consumption: 0
}

const InlineDatapointFormField = (props: InlineDatapointFormFieldProps) => {
  const { value = DEFAULT_DATAPOINT, onChange, onDelete, disabled } = props

  const handleDateChange = (date: Date) => {
    onChange?.({ ...value, date: DateTime.fromJSDate(date)! })
  }

  const handleConsumptionChange = (consumption: number) => {
    onChange?.({ ...value, consumption })
  }

  return (
    <Flex align="center" gap="2" className="group" width={'full'} py={'1'}>
      <DatePickerField
        value={value.date.toJSDate()}
        onChange={handleDateChange}
        disabled={disabled}
        className="flex-grow"
      />
      <TextField.Root
        type="number"
        value={value.consumption}
        onChange={(e) => handleConsumptionChange(e.target.valueAsNumber)}
        disabled={disabled}
        placeholder="consommation"
        className="flex-grow"
      >
        <TextField.Slot side="right">
          <span className="text-gray-10">
            m<sup>3</sup>
          </span>
        </TextField.Slot>
      </TextField.Root>
      <IconButton
        type="button"
        variant="ghost"
        color="gray"
        disabled={disabled}
        onClick={onDelete}
        aria-label="Supprimer le point de données"
        className="group-hover:opacity-100 opacity-0 h-max"
      >
        <LuX size={16} />
      </IconButton>
    </Flex>
  )
}

const useHasDuplicateDates = (datapoints: ManualDatapoint[]) => {
  return useMemo(() => {
    const seen = new Set<string>()
    for (const dp of datapoints) {
      const dateStr = dp.date.toISODate()
      if (!dateStr) continue
      if (seen.has(dateStr)) return true
      seen.add(dateStr)
    }
    return false
  }, [datapoints])
}

type HistoricalDatapointsSectionProps = {
  source?: ManualDataSource
  onChange: (datapoints: ManualDatapoint[], sourceChanges?: Partial<ManualDataSource>) => void
  session: string
  unit: DataSource['unit']
  stationID: string
}

const HistoricalDatapointsSection = ({
  source,
  onChange,
  session,
  unit,
  stationID
}: HistoricalDatapointsSectionProps) => {
  const org = useCurrentOrg()
  const { t } = useTranslation('sites')
  const [datapoints, setDatapoints] = useState<ManualDatapoint[]>([])

  const { data: loadedDataPoints = [], isLoading } = useQuery({
    queryKey: ['data-lake', { deviceId: source?.id ?? 'no-source-no-datapoints' }],
    retry: false,
    queryFn: async () => {
      if (!source) {
        return Promise.resolve([]) // No data points if no source
      }

      const dps = await listDatapointsPayloadByDeviceID<number>({
        orgID: org.id,
        deviceID: source.id,
        // hardcoding the mappingQuery is fine here since we know the source is manual
        mappingQuery: 'payload -> consumption'
      })

      return dps
        .map<ManualDatapoint>((dp) => ({
          date: dp.dateTime,
          consumption: dp.value
        }))
        .sort((a, b) => a.date.toMillis() - b.date.toMillis())
    }
  })
  const wasLoading = usePrevious(isLoading)

  const handleAddDatapoint = () => {
    setDatapoints((prev) => [...prev, { ...DEFAULT_DATAPOINT }])
  }
  const handleDatapointChange = (index: number, value: ManualDatapoint) => {
    setDatapoints((prev) => [...prev.slice(0, index), value, ...prev.slice(index + 1)])
  }
  const handleDatapointDelete = (index: number) => {
    setDatapoints((prev) => prev.filter((_, i) => i !== index))
  }

  useEffect(() => {
    if (datapoints.length === 0 && loadedDataPoints.length > 0 && wasLoading) {
      // Initialize datapoints with loaded data points if empty
      setDatapoints(loadedDataPoints)
    }
  }, [datapoints, loadedDataPoints, wasLoading])

  useEffect(() => {
    onChange(datapoints)
  }, [datapoints])

  useEffect(() => {
    setDatapoints(loadedDataPoints)
  }, [session])

  const hasDuplicateDates = useHasDuplicateDates(datapoints)

  const handleCSVImportConfirm = (importedDatapoints: ManualDatapoint[], csvInfo: OrgAssetMeta) => {
    const notesPrefix = source?.notes ? `${source.notes}\n` : ''
    const updatedSource: Partial<ManualDataSource> = {
      attachments: [...(source?.attachments ?? []), csvInfo.path],
      notes: `${notesPrefix}${t('stations.datasourceSection.csvImportNote', { fileName: csvInfo.name, date: DateTime.now().toISODate() })}`
    }
    onChange(importedDatapoints, updatedSource)
    setDatapoints(importedDatapoints)
  }

  if (isLoading) {
    return <Spinner size="2" />
  }

  if (datapoints.length === 0) {
    return (
      <Card size={'3'}>
        <EmptyState
          title={t('stations.datasourceSection.noDataTitle')}
          description={t('stations.datasourceSection.noDataDescription')}
          afterBody={
            <Flex align={'center'} mt="4" direction={'column'} gap={'2'}>
              <Button onClick={handleAddDatapoint}>{t('stations.datasourceSection.addFirstDatapoint')}</Button>
              <Flex gap={'2'} justify={'center'} align="center" className="w-full">
                <Separator size="1" className="my-2" />
                <Text size="1" color="gray" align="center">
                  {t('stations.datasourceSection.or', 'ou')}
                </Text>
                <Separator size="1" className="my-2" />
              </Flex>
              <CSVImportDialog onConfirm={handleCSVImportConfirm} stationID={stationID} />
            </Flex>
          }
        />
      </Card>
    )
  }

  return (
    <Flex direction="column" gap="3" mt={'3'} className="w-full">
      <Text size="2" weight={'medium'}>
        {t('stations.datasourceSection.historicalDatapointsTitle')}
      </Text>
      <Text size="2" color="gray">
        {t('stations.datasourceSection.historicalDatapointsDescription')}
      </Text>
      <Button
        size={'2'}
        variant="ghost"
        onClick={handleAddDatapoint}
        aria-label={t('stations.datasourceSection.addDatapoint')}
        type="button"
        className="ml-auto mr-6 mt-4"
      >
        <LuPlus size={18} />
        {t('stations.datasourceSection.addDatapoint')}
      </Button>

      <div>
        {datapoints.map((dp, index) => (
          <InlineDatapointFormField
            key={index}
            value={dp}
            onChange={(value) => handleDatapointChange(index, value)}
            onDelete={() => handleDatapointDelete(index)}
            unit={unit}
          />
        ))}
      </div>
      {hasDuplicateDates && (
        <Callout.Root color="amber" variant="soft" className="mt-2">
          <Callout.Text>{t('stations.datasourceSection.duplicateDateError')}</Callout.Text>
        </Callout.Root>
      )}
    </Flex>
  )
}

type ManualDataSourceFormProps = {
  stationID: string
  source?: ManualDataSource
  onSubmit: (source: ManualDataSource, datapoints: ManualDatapoint[]) => void
  isPending: boolean
  onCancel?: () => void
}

const ManualDataSourceForm = ({ source, onSubmit, isPending, stationID, onCancel }: ManualDataSourceFormProps) => {
  const schema = source ? ManualDataSourceSchema : ManualDataSourceSchema.omit({ id: true, createdAt: true })
  const { t } = useTranslation('sites')
  const [datapoints, setDatapoints] = useState<ManualDatapoint[]>([])
  const [editingSession, setEditingSession] = useState(uniqueId())

  // Check for duplicate dates in datapoints
  const hasDuplicateDates = useHasDuplicateDates(datapoints)

  const {
    handleSubmit,
    control,
    formState: { isValid, isDirty },
    watch,
    reset,
    setValue
  } = useForm<ManualDataSource>({
    resolver: zodResolver(schema),
    defaultValues: source || {
      sourceType: 'manual',
      stationID: stationID,
      notes: '',
      readingMethod: 'cumulative',
      unit: 'm3',
      attachments: [],
      config: {}
    },
    mode: 'onChange'
  })

  const handleReset = () => {
    reset()
    setEditingSession(uniqueId())
    onCancel?.()
  }

  const internalOnSubmit = handleSubmit((formData) => onSubmit(formData, datapoints))
  const unit = watch('unit')
  const attachments = watch('attachments') ?? []

  const handleDataPointsChange = (
    newDatapoints: ManualDatapoint[],
    sourceChanges: Partial<ManualDataSource> = {}
  ) => {
    setDatapoints(newDatapoints)

    Object.entries(sourceChanges).forEach(([key, value]) => {
      if (value !== undefined) {
        setValue(key as keyof ManualDataSource, value, { shouldValidate: true, shouldDirty: true })
      }
    })
  }

  return (
    <Flex direction="column" className="w-full">
      <Text size={'3'} weight={'medium'} mt={'3'}>
        {t('stations.datasourceSection.manualEntryTitle')}
      </Text>
      <form onSubmit={internalOnSubmit}>
        <Flex gap={'5'} mt="3">
          <Flex gap="4" direction={'column'} flexGrow={'1'} maxWidth={'50%'}>
            <FieldWrapper label={t('stations.meter.form.readingMethod')} htmlFor="readingMethod">
              <Controller
                control={control}
                name="readingMethod"
                render={({ field }) => (
                  <Select.Root value={field.value} onValueChange={field.onChange}>
                    <Select.Trigger />
                    <Select.Content>
                      <Select.Item value="cumulative">
                        {t('stations.datasourceSection.readingMethodCumulative')}
                      </Select.Item>
                      <Select.Item value="incremental">
                        {t('stations.datasourceSection.readingMethodIncremental')}
                      </Select.Item>
                    </Select.Content>
                  </Select.Root>
                )}
              />
            </FieldWrapper>
            <FieldWrapper label={t('stations.meter.form.measurementUnit')} htmlFor="unit">
              <TextField.Root
                variant="surface"
                placeholder={t('stations.meter.form.measurementUnitPlaceholder')}
                readOnly
                value={watch('unit')}
              />
            </FieldWrapper>
            <HistoricalDatapointsSection
              onChange={handleDataPointsChange}
              source={source}
              session={editingSession}
              unit={unit}
              stationID={stationID}
            />
          </Flex>
          <Flex gap="4" direction={'column'} flexGrow={'1'} maxWidth={'50%'}>
            <FieldWrapper label={t('stations.datasourceSection.notesLabel')}>
              <Controller
                control={control}
                name="notes"
                render={({ field }) => (
                  <TextArea
                    rows={4}
                    placeholder={t('stations.datasourceSection.notesPlaceholder')}
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                )}
              />
            </FieldWrapper>
            {attachments?.length > 0 && (
              <Flex direction={'column'} gap={'2'}>
                <Text size={'2'} weight={'medium'}>
                  {t('stations.datasourceSection.attachmentsTitle')}
                </Text>
                {attachments.map((attachment) => (
                  <AttachmentPreview key={attachment} path={attachment} />
                ))}
              </Flex>
            )}
          </Flex>
        </Flex>
        <Flex justify={'end'} mt={'5'} align={'center'} gap={'5'}>
          <Button
            type="reset"
            size={'3'}
            variant={'ghost'}
            color="gray"
            disabled={!!source && !isDirty}
            onClick={handleReset}
            className="py-2"
          >
            {t('stations.meter.form.cancel')}
          </Button>

          <Button
            size={'3'}
            type="submit"
            loading={isPending}
            disabled={!isValid || !datapoints.length || hasDuplicateDates}
          >
            {source ? t('stations.datasourceSection.updateButton') : t('stations.datasourceSection.importButton')}
          </Button>
        </Flex>
      </form>
    </Flex>
  )
}

export const CreateManualDataSourceForm = (
  props: Omit<ManualDataSourceFormProps, 'source' | 'onSubmit' | 'isPending'> & { onSuccess?: () => void }
) => {
  const org = useCurrentOrg()
  const queryClient = useQueryClient()
  const { t } = useTranslation('sites')

  const mutation = useMutation({
    mutationFn: async ({ source, datapoints }: { source: ManualDataSource; datapoints: ManualDatapoint[] }) => {
      const inserted = await createOrUpdateDatasource(source)
      await insertManualDatapoints({
        orgID: org.id,
        datasourceID: inserted.id,
        datapoints: datapoints.map((dp) => ({
          consumption: dp.consumption,
          date: dp.date
        }))
      })
      return
    },
    onSuccess: () => {
      toast.success(t('stations.datasourceSection.historicalDatapointsSuccess'))
      queryClient.invalidateQueries({ queryKey: ['station-datasources', props.stationID] })
      props.onSuccess?.()
    },
    onError: (error) => {
      console.error('Error creating manual data source:', error)
      Sentry.captureException(error)
      toast.error(t('stations.datasourceSection.historicalDatapointsError'))
    }
  })

  const handleSubmit: ManualDataSourceFormProps['onSubmit'] = (source, datapoints) => {
    mutation.mutate({ source, datapoints })
  }

  return (
    <ManualDataSourceForm {...props} source={undefined} isPending={mutation.isPending} onSubmit={handleSubmit} />
  )
}

export const UpdateManualDataSourceForm = (
  props: Omit<ManualDataSourceFormProps, 'onSubmit' | 'isPending' | 'onCancel'>
) => {
  const org = useCurrentOrg()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({ source, datapoints }: { source: ManualDataSource; datapoints: ManualDatapoint[] }) => {
      const inserted = await createOrUpdateDatasource(source)
      await deleteManualDatapoints({
        orgID: org.id,
        datasourceID: inserted.id
      })
      await insertManualDatapoints({
        orgID: org.id,
        datasourceID: inserted.id,
        datapoints: datapoints.map((dp) => ({
          consumption: dp.consumption,
          date: dp.date
        }))
      })

      return
    },
    onSuccess: () => {
      toast.success('Données historiques mise à jour avec succès')
      queryClient.invalidateQueries({ queryKey: ['station-datasources', props.stationID] })
    },
    onError: (error) => {
      toast.error('Erreur lors de la mise à jour des données historiques')
      Sentry.captureException(error)
    }
  })

  const handleSubmit: ManualDataSourceFormProps['onSubmit'] = (source, datapoints) => {
    mutation.mutate({ source, datapoints })
  }

  return <ManualDataSourceForm {...props} isPending={false} onSubmit={handleSubmit} />
}
